import { useState, useEffect, useCallback } from "react";
import {
  getSwarmStatus, startSwarm, stopSwarm, runSwarmOnce, runAgent,
  GoldFinding, getVaultSummary, VaultEntry, predictFinding,
  getFreeStack, triggerFreeChannels, triggerFreeRescore,
} from "@/goldhunter/api/entities";
import {
  Zap, Play, Square, RefreshCw, Shield, Globe, Wrench,
  ArrowUpRight, Clock, AlertCircle, CheckCircle2,
  XCircle, Radio, Activity, Sparkles, Cpu, TrendingUp,
} from "lucide-react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const AGENT_META = {
  sentinel: {
    name: "Sentinel",
    codename: "The Watcher",
    icon: Shield,
    color: "#1a73e8",          // Google Blue
    tint:  "rgba(26,115,232,0.10)",
    model: "GPT-5.2",
    focus: "Real-time market signal analysis",
    primaryProvider: "groq+jina",
    primaryLabel: "Groq + Jina (free)",
  },
  scraper: {
    name: "Scraper",
    codename: "The Dredger",
    icon: Globe,
    color: "#f9ab00",          // Yellow
    tint:  "rgba(249,171,0,0.12)",
    model: "Gemini 3 Flash",
    focus: "High-volume web scouring",
    primaryProvider: "google",
    primaryLabel: "Google Gemini",
  },
  janitor: {
    name: "Janitor",
    codename: "The Cleaner",
    icon: Wrench,
    color: "#ea4335",          // Google Red
    tint:  "rgba(234,67,53,0.10)",
    model: "Claude Sonnet",
    focus: "Asset recovery & execution",
    primaryProvider: "groq",
    primaryLabel: "Groq Llama 3.3",
  },
};

const HEALTH_BADGE = {
  primary:  { label: "PRIMARY",  color: "#1e8e3e", bg: "rgba(30,142,62,0.10)" },
  fallback: { label: "FALLBACK", color: "#b06000", bg: "rgba(249,171,0,0.14)" },
  degraded: { label: "DEGRADED", color: "#ea4335", bg: "rgba(234,67,53,0.10)" },
  unknown:  { label: "IDLE",     color: "#5f6368", bg: "rgba(95,99,104,0.08)" },
};

function resolveHealth(agent, meta) {
  const provider = (agent?.provider || "").trim();
  const errored  = hasError(agent);
  if (errored) return { ...HEALTH_BADGE.degraded, provider, model: agent?.model_used };
  if (!provider) return { ...HEALTH_BADGE.unknown, provider, model: agent?.model_used };
  if (provider === meta.primaryProvider) return { ...HEALTH_BADGE.primary, provider, model: agent?.model_used };
  return { ...HEALTH_BADGE.fallback, provider, model: agent?.model_used };
}

const STATUS_MAP = {
  running: { label: "Hunting", color: "#1e8e3e", bg: "rgba(30,142,62,0.10)" },
  seeding: { label: "Seeding", color: "#f9ab00", bg: "rgba(249,171,0,0.12)" },
  locked:  { label: "Vault Locked", color: "#ea4335", bg: "rgba(234,67,53,0.10)" },
  idle:    { label: "Standby",  color: "#5f6368", bg: "rgba(95,99,104,0.08)" },
  error:   { label: "Error",    color: "#ea4335", bg: "rgba(234,67,53,0.10)" },
};

const FINDING_STATUS_ICON = {
  PENDING_EXECUTION: Clock,
  EXECUTED: CheckCircle2,
  COLLECTED: ArrowUpRight,
  REJECTED: XCircle,
};

const FINDING_STATUS_COLOR = {
  PENDING_EXECUTION: "#f9ab00",
  EXECUTED: "#1e8e3e",
  COLLECTED: "#1a73e8",
  REJECTED: "#ea4335",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function hasError(agent) {
  const report = (agent?.last_report || agent?.current_task || "").toLowerCase();
  return report.includes("error") || report.includes("fail") || report.includes("halt");
}

function resolveStatus(agent) {
  if (hasError(agent)) return STATUS_MAP.error;
  if (agent?.status === "running") return STATUS_MAP.running;
  if (agent?.status === "seeding") return STATUS_MAP.seeding;
  if (agent?.status === "locked")  return STATUS_MAP.locked;
  return STATUS_MAP.idle;
}

// ─── AGENT CARD ───────────────────────────────────────────────────────────────

function AgentCard({ agent, meta, onPulse }) {
  const [loading, setLoading] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const status = resolveStatus(agent);
  const Icon = meta.icon;
  const isError = status === STATUS_MAP.error;

  const handlePulse = async () => {
    setLoading(true);
    await onPulse(agent.role);
    setLoading(false);
  };

  return (
    <div className="gm-card p-5 flex flex-col">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
          style={{ background: meta.tint }}
        >
          <Icon size={22} color={meta.color} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[16px] font-medium" style={{ color: "var(--yb-text-primary)" }}>
              {meta.name}
            </h3>
            <span
              className="inline-flex items-center gap-1.5 text-[12px] px-2 py-0.5 rounded-full"
              style={{ background: status.bg, color: status.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }} />
              {status.label}
            </span>
          </div>
          <p className="text-[13px] mt-0.5" style={{ color: "var(--yb-text-secondary)" }}>
            {meta.codename} · {meta.focus}
          </p>
        </div>
      </div>

      {/* Provider health */}
      {(() => {
        const health = resolveHealth(agent, meta);
        const activeProvider = health.provider || "—";
        const activeModel = health.model || meta.model;
        const tooltip =
          `Active provider: ${activeProvider}\n` +
          `Active model: ${activeModel}\n` +
          `Primary path: ${meta.primaryLabel} (${meta.primaryProvider})`;
        return (
          <div
            className="mt-4 px-3 py-2 rounded-md text-[12px] flex items-center justify-between gap-2"
            style={{ background: "var(--yb-surface-2)" }}
            title={tooltip}
          >
            <span
              data-testid={`agent-health-${agent?.role || ""}`}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full tracking-wide"
              style={{ background: health.bg, color: health.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: health.color }} />
              {health.label}
            </span>
            <span className="flex flex-col items-end min-w-0">
              <span
                data-testid={`agent-model-${agent?.role || ""}`}
                className="font-mono font-medium truncate"
                style={{ color: "var(--yb-text-primary)" }}
              >
                {activeModel}
              </span>
              <span
                data-testid={`agent-provider-${agent?.role || ""}`}
                className="font-mono text-[10px] truncate"
                style={{ color: "var(--yb-text-muted)" }}
              >
                via {activeProvider}
              </span>
            </span>
          </div>
        );
      })()}

      {/* Telemetry */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Stat label="Findings" value={agent?.findings_count ?? 0} color={meta.color} />
        <Stat
          label="Errors"
          value={agent?.errors_count ?? 0}
          color={(agent?.errors_count || 0) > 0 ? "#ea4335" : "var(--yb-text-primary)"}
        />
      </div>

      {/* Current task */}
      {agent?.current_task && !isError && (
        <p
          className="mt-3 px-3 py-2 rounded-md text-[12px] truncate"
          style={{ background: meta.tint, color: meta.color }}
          title={agent.current_task}
        >
          {agent.current_task}
        </p>
      )}

      {/* Error console */}
      {isError && (
        <div
          className="mt-3 px-3 py-2 rounded-md text-[12px]"
          style={{ background: "rgba(234,67,53,0.08)", color: "#ea4335", border: "1px solid rgba(234,67,53,0.25)" }}
        >
          {agent?.last_report || agent?.current_task || "Unknown error"}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4">
        <button
          data-testid={`run-${agent?.role}`}
          onClick={handlePulse}
          disabled={loading}
          className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full text-[14px] font-medium transition-colors"
          style={{
            background: meta.color,
            color: "#fff",
            opacity: loading ? 0.7 : 1,
            boxShadow: "var(--yb-shadow-1)",
          }}
        >
          {loading ? <Radio size={16} className="animate-pulse" /> : <Zap size={16} />}
          {loading ? "Pulsing…" : "Emit pulse"}
        </button>
        <button
          onClick={() => setLogsOpen((o) => !o)}
          className="h-10 px-4 rounded-full text-[14px] font-medium transition-colors hover:bg-black/5"
          style={{ color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}
        >
          {logsOpen ? "Hide" : "Logs"}
        </button>
      </div>

      {/* Logs */}
      {logsOpen && (
        <div
          className="mt-3 rounded-md max-h-32 overflow-y-auto px-3 py-2 space-y-1 font-mono text-[12px]"
          style={{ background: "var(--yb-surface-2)", color: "var(--yb-text-secondary)" }}
        >
          {agent?.log_entries?.length ? (
            agent.log_entries.map((e, i) => <p key={i}>· {e}</p>)
          ) : (
            <p style={{ color: "var(--yb-text-muted)" }}>No log entries</p>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-md px-3 py-2" style={{ background: "var(--yb-surface-2)" }}>
      <p className="text-[11px] uppercase tracking-wide" style={{ color: "var(--yb-text-muted)" }}>
        {label}
      </p>
      <p className="text-[22px] font-medium mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

// ─── FINDINGS ────────────────────────────────────────────────────────────────

function FindingRow({ f, onExecute, onPredict }) {
  const meta = AGENT_META[f.agent_role] || AGENT_META.sentinel;
  const StatusIcon = FINDING_STATUS_ICON[f.status] || AlertCircle;
  const sc = FINDING_STATUS_COLOR[f.status] || "var(--yb-text-muted)";
  const isPending = f.status === "PENDING_EXECUTION";

  return (
    <div
      data-testid={`finding-${f.id}`}
      className="gm-card flex items-start gap-4 p-4"
      style={isPending ? { borderLeft: `4px solid ${meta.color}` } : {}}
    >
      <div className="shrink-0 mt-0.5">
        <StatusIcon size={20} style={{ color: sc }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="text-[11px] font-medium px-2 py-0.5 rounded-full"
            style={{ background: meta.tint, color: meta.color }}
          >
            {f.agent_role?.toUpperCase()}
          </span>
          <span className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>
            {f.finding_type} · {f.network}
          </span>
          {f.priority === "high" && (
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "rgba(234,67,53,0.10)", color: "#ea4335" }}
            >
              High priority
            </span>
          )}
        </div>
        <p className="text-[15px] font-medium" style={{ color: "var(--yb-text-primary)" }}>
          {f.title}
        </p>
        <p className="text-[13px] mt-0.5 line-clamp-2" style={{ color: "var(--yb-text-secondary)" }}>
          {f.description}
        </p>
        {f.execution_link && (
          <a
            href={f.execution_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[13px] mt-2 hover:underline"
            style={{ color: meta.color }}
          >
            View on-chain <ArrowUpRight size={14} />
          </a>
        )}
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[20px] font-medium" style={{ color: "#1e8e3e" }}>
          ${Math.round(f.estimated_profit).toLocaleString()}
        </div>
        <div className="flex flex-col gap-1.5 mt-2 items-end">
          {isPending && (
            <button
              data-testid={`execute-${f.id}`}
              onClick={() => onExecute(f)}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 h-8 rounded-full text-white"
              style={{ background: "#1e8e3e" }}
            >
              <Play size={12} /> Execute
            </button>
          )}
          {onPredict && (
            <button
              data-testid={`predict-${f.id}`}
              onClick={() => onPredict(f)}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 h-8 rounded-full"
              style={{
                background: "rgba(167,139,250,0.10)",
                color: "#7c4dff",
                border: "1px solid rgba(124,77,255,0.20)",
              }}
              title="Score conversion likelihood"
            >
              <Sparkles size={12} /> Predict
            </button>
          )}
        </div>
        {f._prediction && (
          <div
            data-testid={`prediction-${f.id}`}
            className="mt-2 text-[12px] max-w-[180px]"
            style={{ color: "#7c4dff" }}
          >
            <div className="font-medium">{f._prediction.score}/100 · {f._prediction.verdict}</div>
            <div className="text-[11px] opacity-70 leading-snug" style={{ color: "var(--yb-text-secondary)" }}>
              {(f._prediction.reasoning || "").slice(0, 90)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function GoldHunter() {
  const [swarm, setSwarm] = useState(null);
  const [findings, setFindings] = useState([]);
  const [vault, setVault] = useState(null);
  const [freeStack, setFreeStack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [findingFilter, setFindingFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [s, f, v, fs] = await Promise.all([
        getSwarmStatus(),
        GoldFinding.list(),
        getVaultSummary(),
        getFreeStack().catch(() => null),
      ]);
      setSwarm(s);
      setFindings(Array.isArray(f) ? f : []);
      setVault(v);
      setFreeStack(fs);
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  const handleStart    = async () => { await startSwarm(); load(); };
  const handleStop     = async () => { await stopSwarm();  load(); };
  const handleRunOnce  = async () => { await runSwarmOnce(); setTimeout(load, 1500); };
  const handleRunAgent = async (role) => { await runAgent(role); setTimeout(load, 1500); };
  const handleRunChannels = async () => { await triggerFreeChannels().catch(console.warn); setTimeout(load, 3000); };
  const handleRunRescore  = async () => { await triggerFreeRescore().catch(console.warn); setTimeout(load, 5000); };

  const handleExecute = async (finding) => {
    await GoldFinding.update(finding.id, { status: "EXECUTED" });
    if (finding.estimated_profit > 0) {
      // An agent's estimate is a forecast, not money. It is logged as pipeline
      // only — income is written when a real payment lands (Stripe webhook).
      await VaultEntry.create({
        source: `Gold Finding: ${finding.title}`,
        amount: finding.estimated_profit,
        entry_type: "pipeline_estimate",
        network: finding.network,
        agent_role: finding.agent_role,
        notes: `Estimated value from Gold-Hunter: ${finding.description?.slice(0, 100)}`,
      });
    }
    load();
  };

  const handlePredict = async (finding) => {
    setFindings((prev) => prev.map((x) =>
      x.id === finding.id ? { ...x, _prediction: { score: "…", verdict: "…", reasoning: "Scoring…" } } : x));
    try {
      const pred = await predictFinding(finding.id);
      setFindings((prev) => prev.map((x) =>
        x.id === finding.id ? { ...x, _prediction: pred } : x));
    } catch (e) {
      setFindings((prev) => prev.map((x) =>
        x.id === finding.id
          ? { ...x, _prediction: { score: 0, verdict: "ERROR", reasoning: e?.response?.data?.detail || e.message } }
          : x));
    }
  };

  const filteredFindings = findingFilter === "all"
    ? findings
    : findings.filter((f) => f.agent_role === findingFilter);

  const agents    = swarm?.agents || [];
  const isRunning = swarm?.swarm_running;

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div
        className="w-6 h-6 rounded-full border-2 animate-spin"
        style={{ borderColor: "var(--yb-border)", borderTopColor: "#1a73e8" }}
      />
    </div>
  );

  return (
    <div data-testid="gold-hunter-page" className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* ── COMMAND HEADER ── */}
      <section className="gm-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-[12px] font-medium uppercase tracking-wider"
                style={{ color: "var(--yb-text-muted)" }}
              >
                Gold-Hunter Swarm
              </span>
              <span
                className="inline-flex items-center gap-1.5 text-[12px] px-2 py-0.5 rounded-full"
                style={{
                  background: isRunning ? "rgba(30,142,62,0.10)" : "rgba(95,99,104,0.08)",
                  color:      isRunning ? "#1e8e3e" : "var(--yb-text-secondary)",
                }}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${isRunning ? "animate-pulse" : ""}`}
                  style={{ background: isRunning ? "#1e8e3e" : "#5f6368" }}
                />
                {isRunning ? "Active" : "Standby"}
              </span>
            </div>
            <h1 className="text-[28px] font-normal" style={{ color: "var(--yb-text-primary)", fontFamily: "'Google Sans', Roboto, sans-serif" }}>
              Autonomous income generation
            </h1>
            <p className="text-[14px] mt-1" style={{ color: "var(--yb-text-secondary)" }}>
              Sentinel · Scraper · Janitor — 24/7 on-chain &amp; off-chain scouring
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="btn-run-once"
              onClick={handleRunOnce}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-[14px] font-medium transition-colors hover:bg-black/5"
              style={{ color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}
            >
              <RefreshCw size={16} /> Run once
            </button>
            {isRunning ? (
              <button
                data-testid="btn-stop-swarm"
                onClick={handleStop}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-[14px] font-medium text-white"
                style={{ background: "#ea4335", boxShadow: "var(--yb-shadow-1)" }}
              >
                <Square size={16} /> Kill swarm
              </button>
            ) : (
              <button
                data-testid="btn-start-swarm"
                onClick={handleStart}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-[14px] font-medium text-white"
                style={{ background: "#1a73e8", boxShadow: "var(--yb-shadow-1)" }}
              >
                <Play size={16} /> Deploy swarm
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── VAULT TELEMETRY ── */}
      {vault && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: `$${Math.round(vault.total_income || 0).toLocaleString()}`, l: "Income received", c: "#1e8e3e" },
            { v: `$${Math.round(vault.pipeline_estimate || 0).toLocaleString()}`, l: "Est. pipeline — not received", c: "#f9ab00" },
            { v: findings.filter((f) => f.status === "PENDING_EXECUTION").length, l: "Pending",   c: "#f9ab00" },
            { v: findings.filter((f) => f.status === "EXECUTED").length,           l: "Executed", c: "#1e8e3e" },
          ].map((s) => (
            <div key={s.l} className="gm-card p-4">
              <p className="text-[12px] uppercase tracking-wide" style={{ color: "var(--yb-text-muted)" }}>
                {s.l}
              </p>
              <p className="text-[28px] font-medium mt-1" style={{ color: s.c }}>{s.v}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── AGENT CARDS ── */}
      <section>
        <h2 className="text-[14px] font-medium uppercase tracking-wider mb-3" style={{ color: "var(--yb-text-muted)" }}>
          Active processes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(agents.length > 0 ? agents : Object.entries(AGENT_META).map(([role]) => ({ role, status: "idle", findings_count: 0, errors_count: 0 })))
            .map((agent) => {
              const meta = AGENT_META[agent.role] || AGENT_META.sentinel;
              return <AgentCard key={agent.role} agent={agent} meta={meta} onPulse={handleRunAgent} />;
            })}
        </div>
      </section>

      {/* ── BLOCKCHAIN NODES ── */}
      {swarm?.blockchain_config && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Base L2", sub: "Chain ID: 8453", color: "#0052FF" },
            { label: "Sui",     sub: "Mainnet",        color: "#4CA2FF" },
          ].map(({ label, sub, color }) => (
            <div
              key={label}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full"
              style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border-soft)" }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: color }} />
              <span className="text-[13px] font-medium" style={{ color: "var(--yb-text-primary)" }}>{label}</span>
              <span className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>{sub}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── GOLD FINDINGS FEED ── */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-[14px] font-medium uppercase tracking-wider" style={{ color: "var(--yb-text-muted)" }}>
            Gold findings
          </h2>
          <div className="flex gap-1">
            {["all", "sentinel", "scraper", "janitor"].map((f) => {
              const active = findingFilter === f;
              return (
                <button
                  key={f}
                  data-testid={`filter-${f}`}
                  onClick={() => setFindingFilter(f)}
                  className="text-[13px] font-medium h-8 px-3 rounded-full transition-colors"
                  style={{
                    background: active ? "rgba(26,115,232,0.10)" : "transparent",
                    color: active ? "#1a73e8" : "var(--yb-text-secondary)",
                    border: `1px solid ${active ? "rgba(26,115,232,0.30)" : "var(--yb-border)"}`,
                  }}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          {filteredFindings.map((f) => (
            <FindingRow key={f.id} f={f} onExecute={handleExecute} onPredict={handlePredict} />
          ))}
          {filteredFindings.length === 0 && (
            <div
              className="gm-card py-12 text-center"
              style={{ color: "var(--yb-text-muted)" }}
            >
              <Activity size={20} className="mx-auto mb-2 opacity-60" />
              No findings match this filter
            </div>
          )}
        </div>
      </section>

      {/* ── FREE STACK PANEL ── */}
      {freeStack && (
        <section className="gm-card p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Cpu size={16} style={{ color: "#1a73e8" }} />
              <h2 className="text-[14px] font-medium uppercase tracking-wider" style={{ color: "var(--yb-text-muted)" }}>
                Free Stack
              </h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRunChannels}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium transition-colors hover:bg-black/5"
                style={{ color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}
              >
                <TrendingUp size={12} /> Run channels
              </button>
              <button
                onClick={handleRunRescore}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium transition-colors hover:bg-black/5"
                style={{ color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}
              >
                <RefreshCw size={12} /> Re-score
              </button>
            </div>
          </div>

          {/* Provider quota bars */}
          <div className="space-y-3 mb-5">
            <p className="text-[12px] uppercase tracking-wider" style={{ color: "var(--yb-text-muted)" }}>
              Provider quota usage (today)
            </p>
            {Object.entries(freeStack.providers || {}).map(([key, prov]) => {
              const used = prov.calls_today || 0;
              const limit = prov.limit || 9999;
              const pct = limit >= 9999 ? 0 : Math.min(100, (used / limit) * 100);
              const barColor = pct > 80 ? "#ea4335" : pct > 50 ? "#f9ab00" : "#1e8e3e";
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12px] font-medium" style={{ color: "var(--yb-text-primary)" }}>
                      {prov.label || key}
                    </span>
                    <span className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
                      {limit >= 9999 ? `${used} calls (unlimited free)` : `${used} / ${limit.toLocaleString()} (${prov.headroom_pct}% headroom)`}
                    </span>
                  </div>
                  {limit < 9999 && (
                    <div
                      className="w-full h-1.5 rounded-full overflow-hidden"
                      style={{ background: "var(--yb-surface-2)" }}
                    >
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: barColor }}
                      />
                    </div>
                  )}
                  <p className="text-[11px] mt-0.5" style={{ color: "var(--yb-text-muted)" }}>
                    {prov.role}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Channel revenue */}
          <div className="mb-5">
            <p className="text-[12px] uppercase tracking-wider mb-2" style={{ color: "var(--yb-text-muted)" }}>
              Channel revenue contribution
            </p>
            {Object.keys(freeStack.channels || {}).length === 0 ? (
              <p className="text-[13px]" style={{ color: "var(--yb-text-muted)" }}>
                No channel data yet — click "Run channels" to collect first data.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(freeStack.channels || {}).map(([key, ch]) => (
                  <div
                    key={key}
                    className="rounded-lg p-3"
                    style={{ background: "var(--yb-surface-2)" }}
                  >
                    <p className="text-[13px] font-medium mb-2" style={{ color: "var(--yb-text-primary)" }}>
                      {ch.label || key}
                    </p>
                    <div className="flex gap-4">
                      <div>
                        <p className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>24h</p>
                        <p className="text-[18px] font-medium" style={{ color: "#1e8e3e" }}>
                          ${Math.round(ch.revenue_24h || 0).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>7d</p>
                        <p className="text-[18px] font-medium" style={{ color: "#1a73e8" }}>
                          ${Math.round(ch.revenue_7d || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Nightly rescore recommendation */}
          {freeStack.last_rescore && (
            <div
              className="rounded-lg p-3"
              style={{
                background: freeStack.last_rescore.switch_advisable
                  ? "rgba(249,171,0,0.08)"
                  : "rgba(30,142,62,0.06)",
                border: `1px solid ${freeStack.last_rescore.switch_advisable ? "rgba(249,171,0,0.25)" : "rgba(30,142,62,0.15)"}`,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={13} style={{ color: freeStack.last_rescore.switch_advisable ? "#f9ab00" : "#1e8e3e" }} />
                <span className="text-[12px] font-medium uppercase tracking-wider" style={{ color: "var(--yb-text-muted)" }}>
                  Nightly rescore — {freeStack.last_rescore.at?.slice(0, 10) || "pending"}
                </span>
                {freeStack.last_rescore.switch_advisable && (
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                    style={{ background: "rgba(249,171,0,0.15)", color: "#f9ab00" }}
                  >
                    Switch advisable
                  </span>
                )}
              </div>
              <p className="text-[13px]" style={{ color: "var(--yb-text-secondary)" }}>
                {freeStack.last_rescore.recommendation?.slice(0, 300) || "No recommendation yet."}
              </p>
              {freeStack.last_rescore.alternative && (
                <p className="text-[12px] mt-1" style={{ color: "var(--yb-text-muted)" }}>
                  Suggested alternative: <strong>{freeStack.last_rescore.alternative}</strong>
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── VAULT BREAKDOWN ── */}
      {vault && (
        <section className="gm-card p-6">
          <h2 className="text-[14px] font-medium uppercase tracking-wider mb-4" style={{ color: "var(--yb-text-muted)" }}>
            Autonomous vault — money actually received
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { label: "By agent",   entries: Object.entries(vault.by_agent || {}),   colorFn: (k) => AGENT_META[k]?.color || "var(--yb-text-secondary)" },
              { label: "By network", entries: Object.entries(vault.by_network || {}), colorFn: (k) => k === "base" ? "#0052FF" : k === "sui" ? "#4CA2FF" : "var(--yb-text-secondary)" },
            ].map(({ label, entries, colorFn }) => (
              <div key={label}>
                <p className="text-[12px] uppercase tracking-wider mb-3" style={{ color: "var(--yb-text-muted)" }}>
                  {label}
                </p>
                {entries.length ? entries.map(([key, amount]) => (
                  <div
                    key={key}
                    className="flex justify-between items-center py-2.5"
                    style={{ borderBottom: "1px solid var(--yb-border-soft)" }}
                  >
                    <span className="text-[13px] font-medium" style={{ color: colorFn(key) }}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                    <span className="text-[14px] font-medium" style={{ color: "var(--yb-text-primary)" }}>
                      ${Math.round(amount).toLocaleString()}
                    </span>
                  </div>
                )) : (
                  <p className="text-[13px]" style={{ color: "var(--yb-text-muted)" }}>No data</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
